
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MemorialInfo } from "../MemorialCreationFlow";

interface InitialScreenProps {
  memorialInfo: MemorialInfo;
  onUpdate: (updates: Partial<MemorialInfo>) => void;
  onNext: () => void;
}

const InitialScreen = ({ memorialInfo, onUpdate, onNext }: InitialScreenProps) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <h1 className="text-2xl font-serif text-brand-primary">
          Who are you creating the memorial for?
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              value={memorialInfo.firstName || ''}
              onChange={(e) => onUpdate({ firstName: e.target.value })}
              placeholder="Enter first name"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              value={memorialInfo.lastName || ''}
              onChange={(e) => onUpdate({ lastName: e.target.value })}
              placeholder="Enter last name"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Have they passed away?</Label>
          <div className="flex gap-4">
            <Button
              type="button"
              variant={memorialInfo.hasPassed ? "default" : "outline"}
              onClick={() => onUpdate({ hasPassed: true })}
            >
              Yes
            </Button>
            <Button
              type="button"
              variant={!memorialInfo.hasPassed ? "default" : "outline"}
              onClick={() => onUpdate({ hasPassed: false })}
            >
              No
            </Button>
          </div>
        </div>

        {/* Double memorial toggle */}
        <div className="space-y-3 rounded-xl bg-[#FAF8F3] p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="doubleMemorial" className="text-base font-medium cursor-pointer">
                This memorial honors two people
              </Label>
              <p className="text-sm text-muted-foreground">
                (e.g. a husband and wife who share a headstone)
              </p>
            </div>
            <Switch
              id="doubleMemorial"
              checked={memorialInfo.isDoubleMemorial || false}
              onCheckedChange={(checked) => onUpdate({
                isDoubleMemorial: checked,
                ...(!checked ? { person2FirstName: '', person2LastName: '', person2HasDifferentLastName: false, person2BirthDate: '', person2DeathDate: '', person2BirthPlace: '', person2DeathPlace: '' } : {})
              })}
            />
          </div>

          {memorialInfo.isDoubleMemorial && (
            <div className="space-y-3 pt-2">
              <div className="space-y-2">
                <Label htmlFor="person2FirstName">Second person's first name <span className="text-red-500">*</span></Label>
                <Input
                  id="person2FirstName"
                  value={memorialInfo.person2FirstName || ''}
                  onChange={(e) => onUpdate({ person2FirstName: e.target.value })}
                  placeholder="Enter first name"
                  required
                />
              </div>

              <div className="flex items-center justify-between gap-4 pt-1">
                <Label htmlFor="differentLastName" className="text-sm font-normal text-muted-foreground cursor-pointer">
                  They had different last names
                </Label>
                <Switch
                  id="differentLastName"
                  checked={memorialInfo.person2HasDifferentLastName || false}
                  onCheckedChange={(checked) => onUpdate({
                    person2HasDifferentLastName: checked,
                    ...(!checked ? { person2LastName: '' } : {}),
                  })}
                />
              </div>

              {memorialInfo.person2HasDifferentLastName ? (
                <div className="space-y-2">
                  <Label htmlFor="person2LastName">Second person's last name</Label>
                  <Input
                    id="person2LastName"
                    value={memorialInfo.person2LastName || ''}
                    onChange={(e) => onUpdate({ person2LastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Last name will be shared ({memorialInfo.lastName || 'enter last name above'})
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <Button
        type="submit"
        className="w-full bg-brand-primary hover:bg-brand-primary/90"
        disabled={!memorialInfo.firstName?.trim() || !memorialInfo.lastName?.trim() || (memorialInfo.isDoubleMemorial && !memorialInfo.person2FirstName?.trim())}
      >
        Next
      </Button>
    </form>
  );
};

export default InitialScreen;
